import { Row, Col, Card, Table, Button, Input, Tooltip, Image, Switch } from "antd";
import { useState, useEffect, useRef } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import ShowTotal from "../../components/ShowTotal";
import ConfirmationBox from "../../components/ConfirmationBox";
import apiPath from "../../constants/apiPath";
import moment from 'moment';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import From from "./From";
import { getSortOrder } from "../../constants/conts";
import notfound from "../../assets/images/no-img.png";
import { PencilLineIcon, Trash2 } from "lucide-react";
const Search = Input.Search;

function Index() {

  const sectionName = "Schemes";
  const routeName = "schemes";

  const api = {
    status: apiPath.statusSchemes,
    addEdit: apiPath.addEditSchemes,
    list: apiPath.listSchemes,
    delete: apiPath.deleteSchemes,
  }

  const [searchText, setSearchText] = useState('');
  const { request } = useRequest()
  const [list, setList] = useState([])
  const { showConfirm } = ConfirmationBox()
  const [loading, setLoading] = useState(false)
  const [refresh, setRefresh] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [sortBy, setSortBy] = useState('');
  const lastFiltersRef = useRef({});
  const debouncedSearchText = useDebounce(searchText, 300);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState();

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (_, { image_url }) => <Image width={60} height={60} src={image_url ? image_url : notfound} />
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "brand"),
      render: (_, { brand }) => {
        return (
          brand ? <span className="cap">{brand?.en_name}</span> : '-'
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "category"),
      render: (_, { category }) => {
        return (
          category ? <span className="cap">{category?.en_name}</span> : '-'
        );
      },
    },
    {
      title: "Start Date",
      key: "start_date",
      dataIndex: "start_date",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "start_date"),
      render: (_, { start_date }) => {
        return (
          moment(start_date).format('DD-MMM-YYYY')
        );
      },
    },
    {
      title: "End Date",
      key: "end_date",
      dataIndex: "end_date",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "end_date"),
      render: (_, { end_date }) => {
        return (
          moment(end_date).format('DD-MMM-YYYY')
        );
      },
    },
    {
      title: "Status",
      key: "is_active",
      filters: [
        {
          text: 'Active',
          value: true,
        },
        {
          text: 'Inactive',
          value: false,
        },
      ],
      render: (_, { is_active, _id }) => {
        return (
          <Switch
            checked={is_active}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            loading={loading}
            onChange={(checked) =>
              showConfirm({
                record: _id,
                path: api.status,
                // onLoading: () => setLoading(true),
                onSuccess: () => setRefresh((prev) => !prev),
                is_active: checked,
              })
            }
            className={is_active ? "custom-switch-checked" : "custom-switch-unchecked"}
          />
        );
      },
    },
    {
      title: "Created On",
      key: "created_at",
      dataIndex: "created_at",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "created_at"),
      render: (_, { created_at }) => {
        return (
          moment(created_at).format('DD-MMM-YYYY')
        );
      },
    },
    {
      title: "Action",
      fixed: 'right',
      render: (_, record) => {
        return (
          <>
            <Tooltip title={"Update " + sectionName} color={"purple"} key={"update" + routeName}>
              <Button onClick={() => {
                setSelected(record)
                setVisible(true)
              }}>
                <PencilLineIcon size={15} />
              </Button>
            </Tooltip>
            <Tooltip title={"Delete " + sectionName} color={"red"} key={"delete" + routeName}>
              <Button
                danger
                className="ml-2"
                onClick={() => {
                  showConfirm({
                    record: record._id,
                    path: api.delete,
                    method: 'DELETE',
                    title: 'Delete Scheme',
                    content: 'Are you sure you want to delete this scheme? This action cannot be undone.',
                    okButtonProps: { danger: true },
                    // onLoading: () => setLoading(true),
                    onSuccess: () => setRefresh((prev) => !prev),
                  })
                }}
              >
                <Trash2 size={15} />
              </Button>
            </Tooltip>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    setLoading(true)
    fetchData(pagination, lastFiltersRef.current, sortBy)
  }, [refresh, debouncedSearchText, sortBy, pagination.current, pagination.pageSize])

  const fetchData = (pagination, filters, sortValue = '') => {

    const filterActive = filters ? filters.is_active : null;

    request({
      url:
        api.list +
        `?status=${filterActive ? filterActive.join(',') : ''}` +
        `&page=${pagination.current}` +
        `&limit=${pagination.pageSize}` +
        `&search=${debouncedSearchText}` +
        `&sort=${sortValue || ''}`,
      method: 'GET',
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.list.docs);
        setPagination(prev => ({
          ...prev,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data.data.list.totalDocs,
        }));
      },
      onError: (error) => {
        console.log(error);
        ShowToast(error, Severty.ERROR);
      },
    });
  }

  const handleChange = (pag, filters, sorter) => {
    const { order, field } = sorter || {};

    let nextSort = '';
    if (order) {
      nextSort = `${field}:${order === 'ascend' ? 'asc' : 'desc'}`;
    }

    lastFiltersRef.current = filters || {};

    setSortBy(nextSort);

    setPagination(prev => ({
      ...prev,
      current: pag?.current || 1,
      pageSize: pag?.pageSize || prev.pageSize,
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value)
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant={false}
              className="criclebox tablespace mb-24"
              title={sectionName + " Management"}
              extra={
                <>
                  <Search
                    size="large"
                    onChange={onSearch}
                    value={searchText}
                    onPressEnter={onSearch}
                    placeholder="Search By Brand or Category"
                  />
                  <Button onClick={(e) => { setVisible(true); setSearchText(''); }}>Add New {sectionName}</Button>
                </>
              }
            >
              <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}</h4>
              <div className="table-responsive customPagination">
                <Table
                  loading={loading}
                  columns={columns}
                  dataSource={list}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    responsive: true,
                    defaultPageSize: 20,
                    pageSizeOptions: ['10', '20', '30', '50', '100']
                  }}
                  onChange={handleChange}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Add Modal */}
      {visible &&
        <From
          path={api.addEdit}
          sectionName={sectionName}
          show={visible}
          hide={() => { setSelected(); setVisible(false) }}
          data={selected}
          refresh={() => {
            setRefresh(prev => !prev)
          }}
        />
      }
    </>
  );
}

export default Index;
